import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef } from "react";
import type { CourseNode, CourseNodeContent, UserNodeProgress, ConceptContent, QuestionContent } from "@/types/courses";

interface UseLessonDataReturn {
  node: CourseNode | null;
  steps: CourseNodeContent[];
  userProgress: UserNodeProgress | null;
  isLoading: boolean;
  isError: boolean;
}

export const useLessonData = (courseId: string, nodeId: string): UseLessonDataReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const progressCreated = useRef(false);

  const nodeQuery = useQuery({
    queryKey: ["course-node", nodeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_nodes")
        .select("*")
        .eq("id", nodeId)
        .single();
      if (error) throw error;
      return {
        id: data.id,
        courseId: data.course_id,
        title: data.title,
        description: data.description,
        type: data.type as CourseNode["type"],
        xpReward: data.xp_reward,
        orderIndex: data.order_index,
        prerequisites: data.prerequisites,
      } as CourseNode;
    },
    enabled: !!nodeId,
  });

  const stepsQuery = useQuery({
    queryKey: ["course-node-content", nodeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_node_content")
        .select("*")
        .eq("node_id", nodeId)
        .order("step_index", { ascending: true });
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        nodeId: row.node_id,
        stepIndex: row.step_index,
        type: row.type as "concept" | "question",
        content: row.content as unknown as ConceptContent | QuestionContent,
        xpReward: row.xp_reward,
      })) as CourseNodeContent[];
    },
    enabled: !!nodeId,
  });

  const progressQuery = useQuery({
    queryKey: ["user-node-progress", nodeId],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("user_node_progress")
        .select("*")
        .eq("user_id", user.id)
        .eq("node_id", nodeId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        userId: data.user_id,
        nodeId: data.node_id,
        courseId: data.course_id,
        status: data.status as UserNodeProgress["status"],
        xpEarned: data.xp_earned,
        startedAt: data.started_at,
        completedAt: data.completed_at,
        currentStepIndex: data.current_step_index,
      } as UserNodeProgress;
    },
    enabled: !!user && !!nodeId,
  });

  // Auto-create progress row if none exists
  useEffect(() => {
    if (
      !user ||
      progressQuery.isLoading ||
      progressQuery.data !== null ||
      progressCreated.current
    )
      return;

    progressCreated.current = true;
    (async () => {
      await supabase.from("user_node_progress").insert({
        user_id: user.id,
        node_id: nodeId,
        course_id: courseId,
        status: "in_progress",
        current_step_index: 0,
        started_at: new Date().toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: ["user-node-progress", nodeId] });
    })();
  }, [user, progressQuery.isLoading, progressQuery.data, nodeId, courseId, queryClient]);

  return {
    node: nodeQuery.data ?? null,
    steps: stepsQuery.data ?? [],
    userProgress: progressQuery.data ?? null,
    isLoading: nodeQuery.isLoading || stepsQuery.isLoading || progressQuery.isLoading,
    isError: nodeQuery.isError || stepsQuery.isError || progressQuery.isError,
  };
};
